import React, { useMemo, useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, EyeIcon, XCircleIcon, XIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
// Generate sample RTP requests
const generateRTPRequests = count => {
  const statuses = [{
    name: 'Pending',
    class: 'bg-yellow-100 text-yellow-800'
  }, {
    name: 'Approved',
    class: 'bg-green-100 text-green-800'
  }, {
    name: 'Rejected',
    class: 'bg-red-100 text-red-800'
  }, {
    name: 'Completed',
    class: 'bg-blue-100 text-blue-800'
  }];
  const paymentMethods = ['IBAN', 'ALIAS'];
  const requests = [];
  for (let i = 0; i < count; i++) {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    date.setHours(Math.floor(Math.random() * 24));
    date.setMinutes(Math.floor(Math.random() * 60));
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const paymentMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
    const amount = (Math.random() * 5000 + 100).toFixed(2);
    requests.push({
      id: 'RTP' + Math.floor(Math.random() * 100000000).toString().padStart(8, '0'),
      paymentMethod,
      recipient: paymentMethod === 'IBAN' ? 'PK' + Math.floor(Math.random() * 1000000000000000000000).toString().padStart(22, '0') : '03' + Math.floor(Math.random() * 1000000000).toString().padStart(9, '0'),
      billNumber: 'BILL' + Math.floor(Math.random() * 10000).toString().padStart(4, '0'),
      amount,
      reference: Math.random() > 0.5 ? 'REF' + Math.floor(Math.random() * 10000) : '',
      dateTime: date.toLocaleString(),
      status
    });
  }
  return requests.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
};
const RTPListsPage = () => {
  // Use useMemo to ensure requests are only generated once
  const allRequests = useMemo(() => generateRTPRequests(45), []);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const requestsPerPage = 10;
  // Filter requests
  const filteredRequests = allRequests.filter(request => {
    const matchesSearch = request.id.toLowerCase().includes(searchTerm.toLowerCase()) || request.billNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMethod = !filterMethod || request.paymentMethod === filterMethod;
    const matchesStatus = !filterStatus || request.status.name === filterStatus;
    return matchesSearch && matchesMethod && matchesStatus;
  });
  // Pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginate = pageNumber => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };
  const openDetails = request => {
    // Create a deep copy to ensure the modal shows exact data
    setSelectedRequest({
      ...request,
      status: {
        ...request.status
      }
    });
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };
  return <DashboardLayout data-id="element-1875">
      <div className="mb-6" data-id="element-1876">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1877">
          Request to Pay Lists
        </h1>
        <p className="text-gray-600" data-id="element-1878">
          View and manage all your payment requests
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1879">
        <div className="flex flex-wrap gap-4 mb-6" data-id="element-1880">
          <div className="flex-1 min-w-[200px]" data-id="element-1881">
            <div className="relative" data-id="element-1882">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1883">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1884" />
              </div>
              <input type="text" placeholder="Search by Request ID or Bill Number" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1885" />
            </div>
          </div>
          <div data-id="element-1886">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterMethod} onChange={e => {
            setFilterMethod(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1887">
              <option value="" data-id="element-1888">All Payment Methods</option>
              <option value="IBAN" data-id="element-1889">IBAN</option>
              <option value="ALIAS" data-id="element-1890">ALIAS</option>
            </select>
          </div>
          <div data-id="element-1891">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterStatus} onChange={e => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1892">
              <option value="" data-id="element-1893">All Statuses</option>
              <option value="Pending" data-id="element-1894">Pending</option>
              <option value="Approved" data-id="element-1895">Approved</option>
              <option value="Rejected" data-id="element-1896">Rejected</option>
              <option value="Completed" data-id="element-1897">Completed</option>
            </select>
          </div>
          <div data-id="element-1898">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1899">
              <FilterIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-1900" />
              More Filters
            </button>
          </div>
          <div data-id="element-1901">
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1902">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1903" />
              Export
            </button>
          </div>
        </div>
        {/* RTP Requests Table */}
        <div className="overflow-x-auto" data-id="element-1904">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1905">
            <thead data-id="element-1906">
              <tr data-id="element-1907">
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1908">
                  Request ID
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1909">
                  Date & Time
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1910">
                  Payment Method
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1911">
                  Recipient
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1912">
                  Bill Number
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1913">
                  Amount
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1914">
                  Status
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1915">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1916">
              {currentRequests.map(request => <tr key={request.id} className="hover:bg-gray-50" data-id="element-1917">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1918">
                    {request.id}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1919">
                    <div className="flex items-center" data-id="element-1920">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-1921" />
                      {request.dateTime}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1922">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium" data-id="element-1923">
                      {request.paymentMethod}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500" data-id="element-1924">
                    <div className="max-w-xs truncate" title={request.recipient} data-id="element-1925">
                      {request.recipient}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1926">
                    {request.billNumber}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1927">
                    ₨{request.amount}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-1928">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${request.status.class}`} data-id="element-1929">
                      {request.status.name}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1930">
                    <div className="flex items-center space-x-3" data-id="element-1931">
                      <button onClick={() => openDetails(request)} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-1932">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-1933" />
                        View
                      </button>
                      {request.status.name === 'Pending' && <button className="flex items-center text-red-600 hover:text-red-800" data-id="element-1934">
                          <XCircleIcon className="h-4 w-4 mr-1" data-id="element-1935" />
                          Cancel
                        </button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between" data-id="element-1936">
          <div className="text-sm text-gray-500" data-id="element-1937">
            Showing {indexOfFirstRequest + 1} to{' '}
            {Math.min(indexOfLastRequest, filteredRequests.length)} of{' '}
            {filteredRequests.length} requests
          </div>
          <div className="flex space-x-2" data-id="element-1938">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`flex items-center px-3 py-1 border border-gray-300 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1939">
              <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-1940" />
              Previous
            </button>
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`flex items-center px-3 py-1 border border-gray-300 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1941">
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-1942" />
            </button>
          </div>
        </div>
      </div>
      {/* Request Details Modal */}
      {showModal && selectedRequest && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1943">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1944">
            <div className="flex items-center justify-between p-6 border-b" data-id="element-1945">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-1946">
                Request to Pay Details
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" data-id="element-1947">
                <XIcon className="h-6 w-6" data-id="element-1948" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-1949">
              <div className="grid grid-cols-2 gap-4" data-id="element-1950">
                <div data-id="element-1951">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1952">
                    Request ID
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-1953">
                    {selectedRequest.id}
                  </p>
                </div>
                <div data-id="element-1954">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1955">
                    Date & Time
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-1956">
                    {selectedRequest.dateTime}
                  </p>
                </div>
                <div data-id="element-1957">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1958">
                    Payment Method
                  </h3>
                  <p className="mt-1" data-id="element-1959">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium" data-id="element-1960">
                      {selectedRequest.paymentMethod}
                    </span>
                  </p>
                </div>
                <div data-id="element-1961">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1962">
                    Bill Number
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-1963">
                    {selectedRequest.billNumber}
                  </p>
                </div>
                <div className="col-span-2" data-id="element-1964">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1965">
                    Recipient
                  </h3>
                  <p className="mt-1 text-sm text-gray-900 break-all" data-id="element-1966">
                    {selectedRequest.recipient}
                  </p>
                </div>
                <div data-id="element-1967">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1968">Amount</h3>
                  <p className="mt-1 text-lg font-semibold text-gray-900" data-id="element-1969">
                    ₨{selectedRequest.amount}
                  </p>
                </div>
                <div data-id="element-1970">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1971">Status</h3>
                  <div className="mt-1" data-id="element-1972">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedRequest.status.class}`} data-id="element-1973">
                      {selectedRequest.status.name}
                    </span>
                  </div>
                </div>
                {selectedRequest.reference && <div className="col-span-2" data-id="element-1974">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-1975">
                      Reference
                    </h3>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-1976">
                      {selectedRequest.reference}
                    </p>
                  </div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t" data-id="element-1977">
              <button onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1978">
                Close
              </button>
              {selectedRequest.status.name === 'Pending' && <button className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-1979">
                  Cancel Request
                </button>}
            </div>
          </div>
        </div>}
    </DashboardLayout>;
};
export default RTPListsPage;